/**
 * Pricing Playground mix CSV — settings restore the playground;
 * cost + price blocks are human-readable and ignored on import.
 */

import { EXAMPLE_FORMAT_PRESETS } from '../pricingExampleSolver';

export const MIX_SCHEMA_VERSION = 1;

const FORMAT_KEYS = new Set(EXAMPLE_FORMAT_PRESETS.map((p) => p.key));

const SCALAR_KEYS = new Set([
  'schemaVersion',
  'exported',
  'beer',
  'productId',
  'formatKey',
  'abv',
  'range',
  'productType',
  'anchorField',
  'anchorValue',
  'lockedField',
  'lockedValue',
  'costPerLitre',
]);

const NUMBER_KEYS = new Set([
  'abv',
  'anchorValue',
  'lockedValue',
  'costPerLitre',
]);

const NULLABLE_KEYS = new Set([
  'lockedField',
  'lockedValue',
  'costPerLitre',
  'productId',
  'beer',
  'productType',
]);

const IDENT = /^[A-Za-z][A-Za-z0-9]*$/;

function csvCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function csvRow(cells) {
  return cells.map(csvCell).join(',');
}

function formatCsvNumber(n) {
  if (n == null || n === '') return '';
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

export function isoDateLocal(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function emitScalar(lines, key, value) {
  lines.push(csvRow([key, value == null ? '' : value]));
}

/**
 * @param {object} mix
 * @param {{
 *   costRows?: Array<{label: string, value: number}>,
 *   priceGroups?: Array<{
 *     title: string,
 *     rows: Array<{
 *       label: string,
 *       value: number,
 *       tradeGp?: number|null,
 *       ourGp?: number|null,
 *     }>,
 *   }>,
 * }} [ref]
 */
export function serializeMixToCsv(mix, ref = {}) {
  const lines = [
    '# Pricing Playground mix',
    '# Settings restore the playground on import; everything below is read-only.',
    '',
    csvRow(['Setting', 'Value']),
  ];

  emitScalar(lines, 'schemaVersion', mix.schemaVersion ?? MIX_SCHEMA_VERSION);
  emitScalar(lines, 'exported', mix.exported || isoDateLocal());
  emitScalar(lines, 'beer', mix.productName ?? mix.beer ?? '');
  emitScalar(lines, 'productId', mix.productId ?? '');
  emitScalar(lines, 'formatKey', mix.formatKey ?? '');
  emitScalar(lines, 'abv', mix.abv ?? '');
  emitScalar(lines, 'range', mix.range ?? '');
  emitScalar(lines, 'productType', mix.productType ?? '');
  emitScalar(lines, 'anchorField', mix.anchorField ?? '');
  emitScalar(lines, 'anchorValue', mix.anchorValue ?? '');
  emitScalar(lines, 'lockedField', mix.lockedField ?? '');
  emitScalar(lines, 'lockedValue', mix.lockedValue ?? '');
  emitScalar(lines, 'costPerLitre', mix.costPerLitre ?? '');

  const costOverrides = mix.costOverrides || {};
  Object.keys(costOverrides).sort().forEach((key) => {
    emitScalar(lines, `cost.${key}`, costOverrides[key]);
  });

  const packagingOverrides = mix.packagingOverrides || {};
  Object.keys(packagingOverrides).sort().forEach((formatId) => {
    const components = packagingOverrides[formatId] || {};
    Object.keys(components).sort().forEach((component) => {
      emitScalar(lines, `packaging.${formatId}.${component}`, components[component]);
    });
  });

  const costRows = ref.costRows || [];
  if (costRows.length) {
    lines.push('');
    lines.push(csvRow(['Cost breakdown', '$']));
    costRows.forEach((row) => {
      lines.push(csvRow([row.label, formatCsvNumber(row.value)]));
    });
  }

  (ref.priceGroups || []).forEach((group) => {
    if (!group?.rows?.length) return;
    lines.push('');
    lines.push(csvRow([group.title, 'Price', 'Trade GP %', 'Our GP %']));
    group.rows.forEach((row) => {
      lines.push(csvRow([
        row.label,
        formatCsvNumber(row.value),
        formatCsvNumber(row.tradeGp),
        formatCsvNumber(row.ourGp),
      ]));
    });
  });

  lines.push('');
  return `${lines.join('\n')}`;
}

function parseCsvLine(line) {
  const cells = [];
  let current = '';
  let inQuotes = false;
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 2;
        } else {
          inQuotes = false;
          i += 1;
        }
      } else {
        current += ch;
        i += 1;
      }
    } else if (ch === '"') {
      inQuotes = true;
      i += 1;
    } else if (ch === ',') {
      cells.push(current);
      current = '';
      i += 1;
    } else {
      current += ch;
      i += 1;
    }
  }
  cells.push(current);
  return cells;
}

function parseNumber(raw, key, errors) {
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    errors.push(`Invalid number for ${key}`);
    return undefined;
  }
  return n;
}

function emptyMix() {
  return {
    costOverrides: {},
    packagingOverrides: {},
  };
}

/**
 * @param {string} text
 * @returns {{ mix: object, errors: string[] }}
 */
export function parseMixFromCsv(text) {
  const errors = [];
  const mix = emptyMix();
  const raw = String(text || '').replace(/^\uFEFF/, '');
  const lines = raw.split(/\r?\n/);

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return;

    const cells = parseCsvLine(trimmed);
    const key = String(cells[0] ?? '').trim();
    const value = String(cells[1] ?? '').trim();
    if (!key) return;
    if (key === 'Setting' && value === 'Value') return;

    if (key.startsWith('cost.')) {
      const field = key.slice(5);
      if (!IDENT.test(field)) {
        errors.push(`Invalid cost key ${key}`);
        return;
      }
      if (value === '') return;
      const n = parseNumber(value, key, errors);
      if (n == null) return;
      mix.costOverrides[field] = n;
      return;
    }

    if (key.startsWith('packaging.')) {
      const rest = key.slice('packaging.'.length);
      const dot = rest.lastIndexOf('.');
      if (dot <= 0 || dot === rest.length - 1) {
        errors.push(`Invalid packaging key ${key}`);
        return;
      }
      const formatId = rest.slice(0, dot);
      const component = rest.slice(dot + 1);
      if (!formatId || !IDENT.test(component)) {
        errors.push(`Invalid packaging key ${key}`);
        return;
      }
      if (value === '') return;
      const n = parseNumber(value, key, errors);
      if (n == null) return;
      if (!mix.packagingOverrides[formatId]) {
        mix.packagingOverrides[formatId] = {};
      }
      mix.packagingOverrides[formatId][component] = n;
      return;
    }

    if (!SCALAR_KEYS.has(key)) return;

    if (NULLABLE_KEYS.has(key) && value === '') {
      if (key === 'beer') mix.productName = '';
      else if (key === 'productType') mix.productType = '';
      else mix[key] = null;
      return;
    }

    if (NUMBER_KEYS.has(key)) {
      if (value === '') return;
      const n = parseNumber(value, key, errors);
      if (n == null) return;
      mix[key] = n;
      return;
    }

    if (key === 'beer') {
      mix.productName = value;
      return;
    }

    if (key === 'formatKey') {
      if (!FORMAT_KEYS.has(value)) {
        errors.push(`Unknown formatKey: ${value}`);
        return;
      }
      mix.formatKey = value;
      return;
    }

    if (key === 'schemaVersion' || key === 'exported') return;

    mix[key] = value;
  });

  if (!mix.formatKey) {
    errors.push('Missing formatKey');
  }

  return { mix, errors };
}

export function downloadCsv(filename, text) {
  const blob = new Blob([`\uFEFF${text}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
